import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PaymentSection = ({ paymentInfo, userAttendance, isAdmin }) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('pix');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const handlePayment = async (method) => {
    setIsProcessingPayment(true);
    
    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    if (method === 'pix') {
      // Generate PIX QR code or show payment instructions
      alert('PIX: Use a chave PIX ou QR code para fazer o pagamento');
    } else if (method === 'credit') {
      // Process credit card payment
      alert('Redirecionando para processamento do cartão...');
    }
    
    setIsProcessingPayment(false);
    setShowPaymentForm(false);
  };

  const copyPixKey = async () => {
    try {
      await navigator?.clipboard?.writeText(paymentInfo?.pixKey);
      // Show toast notification
      alert('Chave PIX copiada!');
    } catch (err) {
      console.error('Failed to copy PIX key:', err);
    }
  };

  if (!paymentInfo) return null;

  const paymentProgress = (paymentInfo?.paidAmount / paymentInfo?.totalAmount) * 100;

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-card-foreground">
          Pagamento
        </h3>
        
        {isAdmin && (
          <Button
            variant="ghost"
            size="sm"
            iconName="Settings"
            onClick={() => {
              // Handle payment settings
              console.log('Payment settings');
            }}
          />
        )}
      </div>

      {/* Payment Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-card-foreground">
            Arrecadado
          </span>
          <span className="text-sm font-medium text-card-foreground">
            R$ {paymentInfo?.paidAmount?.toFixed(2)} / R$ {paymentInfo?.totalAmount?.toFixed(2)}
          </span>
        </div>
        
        <div className="w-full bg-muted/50 rounded-full h-3 mb-2">
          <div
            className="bg-gradient-to-r from-success to-success/80 h-3 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(paymentProgress, 100)}%` }}
          />
        </div>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{Math.round(paymentProgress)}% pago</span>
          <span>Faltam R$ {paymentInfo?.pendingAmount?.toFixed(2)}</span>
        </div>
      </div>

      {/* User Payment Status */}
      {userAttendance === 'confirmed' && (
        <div className="mb-6">
          {!showPaymentForm ? (
            <div className="border border-warning/20 bg-warning/5 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex items-center justify-center w-8 h-8 bg-warning/20 rounded-full">
                  <Icon name="CreditCard" size={16} className="text-warning" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-warning mb-1">
                    Pagamento pendente
                  </p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Sua vaga só será garantida após o pagamento de R$ 25,00
                  </p>
                  
                  <Button
                    variant="warning"
                    size="sm"
                    iconName="CreditCard"
                    iconPosition="left"
                    onClick={() => setShowPaymentForm(true)}
                    fullWidth
                  >
                    Pagar Agora
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-border rounded-lg p-4">
              <h4 className="font-medium text-card-foreground mb-4">
                Escolha a forma de pagamento
              </h4>

              {/* Payment Method Selection */}
              <div className="space-y-3 mb-4">
                <div
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedPaymentMethod === 'pix' ?'border-primary bg-primary/5' :'border-border hover:bg-muted/30'
                  }`}
                  onClick={() => setSelectedPaymentMethod('pix')}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      selectedPaymentMethod === 'pix' ?'border-primary bg-primary' :'border-muted-foreground'
                    }`}>
                      {selectedPaymentMethod === 'pix' && (
                        <div className="w-2 h-2 bg-white rounded-full m-0.5" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <Icon name="Smartphone" size={16} className="text-primary" />
                        <span className="font-medium text-card-foreground">PIX</span>
                        <span className="text-xs bg-success/10 text-success px-2 py-0.5 rounded-full">
                          Instantâneo
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Pagamento via PIX - Confirmação imediata
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedPaymentMethod === 'credit' ?'border-primary bg-primary/5' :'border-border hover:bg-muted/30'
                  }`}
                  onClick={() => setSelectedPaymentMethod('credit')}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      selectedPaymentMethod === 'credit' ?'border-primary bg-primary' :'border-muted-foreground'
                    }`}>
                      {selectedPaymentMethod === 'credit' && (
                        <div className="w-2 h-2 bg-white rounded-full m-0.5" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <Icon name="CreditCard" size={16} className="text-primary" />
                        <span className="font-medium text-card-foreground">Cartão de Crédito</span>
                        <span className="text-xs bg-info/10 text-info px-2 py-0.5 rounded-full">
                          Parcelado
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Cartão de crédito - Até 3x sem juros
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* PIX Payment Details */}
              {selectedPaymentMethod === 'pix' && (
                <div className="bg-muted/30 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-card-foreground">
                      Chave PIX
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Copy"
                      onClick={copyPixKey}
                      className="h-6 px-2 text-xs"
                    >
                      Copiar
                    </Button>
                  </div>
                  
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Smartphone" size={14} className="text-muted-foreground" />
                    <span className="text-sm font-mono text-card-foreground">
                      {paymentInfo?.pixKey}
                    </span>
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    Favorecido: {paymentInfo?.bankDetails?.account} • {paymentInfo?.bankDetails?.bank}
                  </p>
                </div>
              )}

              {/* Payment Actions */}
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPaymentForm(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                
                <Button
                  variant="default"
                  size="sm"
                  iconName={selectedPaymentMethod === 'pix' ? 'Smartphone' : 'CreditCard'}
                  iconPosition="left"
                  onClick={() => handlePayment(selectedPaymentMethod)}
                  loading={isProcessingPayment}
                  className="flex-1"
                >
                  {isProcessingPayment ? 'Processando...' : `Pagar R$ 25,00`}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Summary for Admin */}
      {isAdmin && (
        <div className="space-y-3">
          <h4 className="font-medium text-card-foreground">
            Resumo de Pagamentos
          </h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-success/10 border border-success/20 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-success">6</div>
              <div className="text-xs text-success">Pagaram</div>
            </div>
            
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-warning">2</div>
              <div className="text-xs text-warning">Pendentes</div>
            </div>
          </div>

          <div className="pt-3 border-t border-border">
            <div className="flex space-x-3">
              <Button
                variant="outline"
                size="sm"
                iconName="Send"
                iconPosition="left"
                className="flex-1"
                onClick={() => {
                  // Send payment reminders
                  alert('Lembrete de pagamento enviado para jogadores pendentes');
                }}
              >
                Enviar Lembretes
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                className="flex-1"
                onClick={() => {
                  // Export payment report
                  console.log('Export payment report');
                }}
              >
                Relatório
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Information for Non-participants */}
      {userAttendance !== 'confirmed' && !isAdmin && (
        <div className="text-center py-4">
          <Icon name="Lock" size={24} className="text-muted-foreground mb-2 mx-auto" />
          <p className="text-sm text-muted-foreground">
            Confirme sua presença para acessar as opções de pagamento
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentSection;